import { z } from 'zod';

type T = (key: string) => string;

export const createClientSchema = (t: T) =>
  z
    .object({
      name: z.string().min(1, t('nameRequired')).max(100, t('nameMax')),
      admin_email: z
        .string()
        .min(1, t('emailRequired'))
        .email(t('emailInvalid')),
      password: z
        .string()
        .min(8, t('passwordMin'))
        .regex(/[A-Z]/, t('passwordUppercase'))
        .regex(/[^A-Za-z0-9]/, t('passwordSpecial')),
      confirm_password: z.string().min(1, t('confirmPasswordRequired')),
      notes: z.string().max(2000, t('notesMax')).default(''),
      logo: z
        .instanceof(File)
        .refine((f) => f.size <= 5 * 1024 * 1024, t('maxFileSize'))
        .nullable()
        .optional()
    })
    .refine((data) => data.password === data.confirm_password, {
      message: t('passwordsMismatch'),
      path: ['confirm_password']
    });

export const updateClientSchema = (t: T) =>
  z
    .object({
      name: z.string().min(1, t('nameRequired')).max(100, t('nameMax')),
      admin_email: z
        .string()
        .min(1, t('emailRequired'))
        .email(t('emailInvalid')),
      password: z
        .string()
        .min(8, t('passwordMin'))
        .regex(/[A-Z]/, t('passwordUppercase'))
        .regex(/[^A-Za-z0-9]/, t('passwordSpecial'))
        .optional()
        .or(z.literal('')),
      confirm_password: z.string().optional().or(z.literal('')),
      notes: z.string().max(2000, t('notesMax')).default(''),
      logo: z
        .instanceof(File)
        .refine((f) => f.size <= 5 * 1024 * 1024, t('maxFileSize'))
        .nullable()
        .optional()
    })
    .refine(
      (data) => !data.password || data.password === data.confirm_password,
      {
        message: t('passwordsMismatch'),
        path: ['confirm_password']
      }
    );

export const clientSchema = createClientSchema((key) => {
  const defaults: Record<string, string> = {
    nameRequired: 'Company name is required.',
    nameMax: 'Company name must not exceed 100 characters.',
    emailRequired: 'Admin email is required.',
    emailInvalid: 'Please enter a valid email address.',
    passwordMin: 'Password must be at least 8 characters.',
    passwordUppercase: 'Password must contain at least 1 uppercase letter.',
    passwordSpecial: 'Password must contain at least 1 special symbol.',
    confirmPasswordRequired: 'Please confirm the password.',
    passwordsMismatch: 'Passwords do not match.',
    notesMax: 'Notes must not exceed 2000 characters.',
    maxFileSize: 'Max file size is 5MB.'
  };
  return defaults[key] ?? key;
});

export type ClientFormValues = z.infer<typeof clientSchema>;
export type ClientUpdateFormValues = z.infer<
  ReturnType<typeof updateClientSchema>
>;
